import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { combineLatest, Observable, of } from "rxjs";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency/fiat.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { Tradable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { TradableWalletComponent } from "@modules/main/components/tradable-wallet.component";
import { TradableService } from "@main-services/currency/tradable.service";
import { TradableWalletService } from "@main-services/wallet/tradable_wallet.service";
import { SegmentService } from "@main-services/segment.service";
import { tradableToArray } from "@helpers/tradable-convert";
import { TradableTransaction } from "@models/transaction";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";
import { TradingOperationType } from "@gql/offer";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { PortfolioRemote } from "@modules/portfolio/infrastructure/portfolio.remote";
import { catchError, map, switchMap } from "rxjs/operators";
import { RudderstackService } from "@main-services/rudderstack.service";
import { isDigiAsset } from "@modules/trade/helpers/helper";
import { TooltipTradableAction } from "@main-services/tooltip.service";
import { RealStocksFacade } from "@modules/real-stocks-temp/real-stocks.facade";
import { config } from "@config";
import { BFFSecurityAsset } from "@models/asset/bff_security_asset";
import { DYNAMIC_CONFIGS, RS_REDIRECTION_TYPE, StatsigClientService } from "@core-services/statsig/statsig.service";

interface AdvTradingReservedFunds {
	isAdvancedTradingEnabled: boolean;
	reservedFunds: number;
}

@Component({
	templateUrl: "./tradable-wallet-transaction.component.html",
	styleUrls: ["./tradable-wallet-transaction.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TradableWalletTransactionComponent
	extends TradableWalletComponent<TradableTransaction, Tradable, TradableWallet>
	implements OnInit
{
	public readonly transactionFilters: string[] = ["buy", "sell", "deposit", "withdrawal", "transfer"];
	public readonly trackingBasePath: string = "/wallet";
	public pendingTrades$: Observable<TradingOperationType[]>;

	private isAdvancedTradingEnabled$: Observable<boolean> = this.launchDarklyService.isActive$(
		LD_FLAG.ADVANCED_TRADING
	);

	private reservedFunds$: Observable<number> = this.portfolio.getPortfolioOverview().pipe(
		switchMap((overview) => {
			const portfolio = overview.portfolios.find(
				(portfolio) => portfolio.asset_id === this.getWallet().getCurrencyId()
			);
			return this.portfolio.getAssetDetails(portfolio.asset_pid);
		}),
		map((asset) => +asset?.reserved_asset_balance),
		catchError(() => {
			return of(0);
		})
	);

	public isLegacyDigiAsset = false;

	private realSecuritiesRedirectionType$ = this.statsigService.getDynamicConfig<Record<"value", RS_REDIRECTION_TYPE>>(
		DYNAMIC_CONFIGS.RS_REDIRECTION_TYPE
	);
	public REDIRECTION_TYPE = RS_REDIRECTION_TYPE;

	public hasAdvTradingReservedFunds$: Observable<AdvTradingReservedFunds> = combineLatest({
		isAdvancedTradingEnabled: this.isAdvancedTradingEnabled$,
		reservedFunds: this.reservedFunds$
	});

	public isLeveragedToken: boolean = false;

	public constructor(
		protected route: ActivatedRoute,
		protected infoService: InfoService,
		protected masterdataService: MasterdataService,
		protected modalService: ModalService,
		protected router: Router,
		protected tradableListService: TradableListService,
		protected accountService: AccountService,
		protected fiatService: FiatService,
		protected walletListService: TradableWalletListService,
		protected translationService: TranslationService,
		protected segmentService: SegmentService,
		protected rudderstackService: RudderstackService,
		protected activatedRoute: ActivatedRoute,
		private readonly launchDarklyService: LaunchDarklyService,
		private readonly portfolio: PortfolioRemote,
		private readonly realStocksFacade: RealStocksFacade,
		private readonly statsigService: StatsigClientService
	) {
		super(
			route,
			router,
			tradableListService,
			walletListService,
			translationService,
			accountService,
			activatedRoute
		);
	}

	public ngOnInit(): void {
		const currency = this.getCurrency();
		this.isLegacyDigiAsset = currency ? isDigiAsset(currency.getCurrencyType()) : false;
		this.init("wallet/" + this.walletId + "/transactions");
		if (currency) {
			this.pendingTrades$ = this.walletService.getPendingTradesByAssetId(this.getCurrency().getPid());
		}
		this.isLeveragedToken =
			this.tradableListService.getItemById(this.getWallet().getCurrencyId()) instanceof LeveragedToken;
		super.ngOnInit();
	}

	protected get currencyService(): TradableService<Tradable> {
		return this.tradableListService.serviceForId(this.getWallet()?.getCurrencyId());
	}

	protected get walletService(): TradableWalletService<TradableWallet, TradableTransaction, Tradable> {
		return this.walletListService.serviceForId(this.walletId);
	}

	public getButtonActions(): string[] {
		if (this.isLegacyDigiAsset) {
			return super
				.getButtonActions()
				.filter((action) => action !== TooltipTradableAction.BUY && action !== TooltipTradableAction.SAVINGS);
		}
		return super.getButtonActions();
	}

	public getSplitBalance(): string[] {
		const wallet: TradableWallet = this.getWallet();
		if (wallet) {
			const currency = this.getCurrency();
			if (currency) {
				if (currency.getPrecisionForTx() > 0) {
					return tradableToArray(
						this.tradablePrecisionPipe.transform(wallet.getBalance(), {
							id: wallet.getCurrencyId(),
							txPrecision: true
						}),
						currency
					);
				}
				return tradableToArray(wallet.getBalance(), currency, true);
			}
		}
		return ["", "", ""];
	}

	protected getRSFeeText(): string {
		return this.realStocksFacade.getRSFeeFormatted();
	}

	public getV4RedirectURL(): string {
		const asset = this.getCurrency() as BFFSecurityAsset;
		if (asset) {
			const pathName = asset.getV4PathName();
			if (pathName === null) {
				return config.V4AppBaseUrl;
			}

			return config.V4AppBaseUrl + pathName + asset.getPid();
		}

		return config.V4AppBaseUrl;
	}
}
